import { HttpUrlEncodingCodec } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, ElementRef, OnChanges, OnDestroy, input } from '@angular/core';
import { Observable, ReplaySubject, Subject, takeUntil } from 'rxjs';

import { GoogleTagManagerFacade } from 'ish-core/facades/gtm.facade';

import { DotCmsFacade } from '../../../facades/dot-cms.facade';
import { CustomDotCmsContentlet } from '../../../models/contentlet/custom-dotcms-contentlet.model';
import { DotCmsContentTypeComponent } from '../../../models/dot-cms-content-types-component.model';
import { setupIntersectionObserver } from '../../../utils/dot-cms-utils';

@Component({
  selector: 'custom-dot-cms-image',
  templateUrl: './dot-cms-image.component.html',
  styleUrls: ['./dot-cms-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DotCmsImageComponent implements DotCmsContentTypeComponent, OnChanges, OnDestroy {
  constructor(
    private dotCmsFacade: DotCmsFacade,
    private elementRef: ElementRef,
    private gtmFacade: GoogleTagManagerFacade
  ) {}
  contentlet: CustomDotCmsContentlet;
  readonly identifier = input<string>(undefined);
  contentlet$: Observable<CustomDotCmsContentlet>;
  host$: Observable<string>;
  codec = new HttpUrlEncodingCodec();
  private intersectionObserver: IntersectionObserver;
  animationTriggered$ = new ReplaySubject<boolean>(1);
  private destroy$ = new Subject<void>();
  isElementCreated = true;
  triggerCount = 1;

  ngOnChanges() {
    this.contentlet$ = this.dotCmsFacade.getContentletByIdentifier$(this.identifier());
    this.host$ = this.dotCmsFacade.getDotCmsHost$;
    this.intersectionObserver = setupIntersectionObserver(this.elementRef.nativeElement, this.animationTriggered$);
    this.contentlet$.pipe(takeUntil(this.destroy$)).subscribe(contentlet => {
      this.contentlet = contentlet;
      this.gtmFacade.setupIntersectionObserverForGTMEvent(
        this.elementRef.nativeElement,
        [contentlet],
        this.triggerCount,
        'view_promotion'
      );
    });
  }
  buildImageSrc(imageKey: string) {
    return this.dotCmsFacade.generateImageURIFromImageKey(this.identifier(), imageKey);
  }
  getImageAnimationCss(animation: string, identifier?: string, url?: string) {
    this.resetImageComponentStructure(identifier);
    switch (animation) {
      case '1': {
        if (this.isElementCreated) {
          this.getShineAnimationContainer(identifier);
          return '';
        }
      }
      case '2': {
        return 'image-flashing';
      }
      case '3': {
        return 'image-opacity';
      }
      case '4': {
        return 'image-greyscale';
      }
      case '5': {
        return 'image-zoom';
      }
      case '6': {
        this.getFrameAnimationContainer(identifier, url);
        return '';
      }
    }
  }

  getShineAnimationContainer(identifier: string) {
    const imageContainer = document.getElementById(`image-container-${identifier}`);
    const imageElement = document.getElementById(`image-${identifier}`);
    const figureElement = document.createElement('figure');
    figureElement.classList.add('image-shine');
    figureElement.appendChild(imageElement);
    imageContainer.appendChild(figureElement);
    this.isElementCreated = false;
  }

  getFrameAnimationContainer(identifier?: string, url?: string) {
    const figureElement = this.isElementCreated ? document.createElement('figure') : undefined;
    if (figureElement) {
      figureElement.style.marginBottom = '0px';
    }
    const componentWidth = this.elementRef.nativeElement.offsetWidth;
    const slides = componentWidth / 8;
    const imageContainer = document.getElementById(`image-container-${identifier}`);
    const imageElement = document.getElementById(`image-${identifier}`);
    imageElement.style.transform = 'none';
    imageContainer.classList.add('frame');
    let slide = 0;
    for (let i = 1; i < 10; i++) {
      if (this.isElementCreated) {
        if (i === 1) {
          imageElement.classList.add('image-1');
          figureElement.appendChild(imageElement);
        } else {
          if (i === 2) {
            const divElement = document.createElement('div');
            divElement.classList.add('image-2');
            divElement.style.width = `${slides}px`;
            divElement.style.backgroundPosition = `${slide}px 0px`;
            divElement.style.backgroundImage = `url(${url})`;
            divElement.style.left = `0px`;
            figureElement.appendChild(divElement);
            slide = slide + slides;
          } else {
            const divElement = document.createElement('div');
            divElement.classList.add('image-2');
            divElement.style.width = `${slides}px`;
            divElement.style.backgroundPosition = `-${slide}px 0px`;
            divElement.style.left = `${slide}px`;
            divElement.style.backgroundImage = `url(${url})`;
            figureElement.appendChild(divElement);
            slide = slide + slides;
          }
        }
      }
    }
    if (figureElement) {
      imageContainer?.appendChild(figureElement);
    }
    this.isElementCreated = false;
  }
  resetImageComponentStructure(identifier: string) {
    const imageContainer = document.getElementById(`image-container-${identifier}`);
    const imageElement = document.getElementById(`image-${identifier}`);
    if (imageElement.querySelectorAll('.image-1')) {
      imageElement.removeAttribute('style');
      imageElement.classList.remove('image-1');
    }
    if (imageContainer) {
      while (imageContainer.firstChild) {
        imageContainer.removeChild(imageContainer.firstChild);
      }
    }
    imageContainer.appendChild(imageElement);
    this.isElementCreated = true;
  }
  selectPromotionGTMEvent() {
    this.gtmFacade.selectPromotionGTMEvent(this.contentlet?.link, this.contentlet);
  }
  ngOnDestroy() {
    this.intersectionObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
